import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTenantContext } from './TenantContext';
import { useAuth } from '@/hooks/useApi';
import { apiClient } from '@/lib/api';

interface ThemeColors {
  primary: string;
  secondary: string;
  background: string;
  text: string;
}

interface ThemeContextType {
  colors: ThemeColors;
  isLoading: boolean;
  applyTheme: (colors: ThemeColors) => void;
}

const defaultColors: ThemeColors = {
  primary: '#3C226E',
  secondary: '#9CFF00', 
  background: '#FFFFFF',
  text: '#333333'
};

const ThemeContext = createContext<ThemeContextType>({
  colors: defaultColors,
  isLoading: false,
  applyTheme: () => {}
});

export const useTheme = () => useContext(ThemeContext);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [colors, setColors] = useState<ThemeColors>(defaultColors);
  const [isLoading, setIsLoading] = useState(false);
  const { selectedTenantId } = useTenantContext();
  const { user } = useAuth();
  
  const activeTenantId = selectedTenantId || user?.tenant_from_group;

  const applyTheme = (newColors: ThemeColors) => {
    console.log('ThemeProvider: Applying theme colors to CSS:', newColors);
    setColors(newColors);
    
    // Apply CSS custom properties to root
    const root = document.documentElement;
    root.style.setProperty('--color-primary', newColors.primary);
    root.style.setProperty('--color-secondary', newColors.secondary);
    root.style.setProperty('--color-background', newColors.background);
    root.style.setProperty('--color-text', newColors.text);
    
    // Apply to button and accent elements
    root.style.setProperty('--color-primary-rgb', hexToRgb(newColors.primary));
    root.style.setProperty('--color-secondary-rgb', hexToRgb(newColors.secondary));
    
    console.log('ThemeProvider: CSS variables set:', {
      '--color-primary': newColors.primary,
      '--color-secondary': newColors.secondary,
      '--color-primary-rgb': hexToRgb(newColors.primary)
    });
  };

  const hexToRgb = (hex: string): string => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return '60, 34, 110'; // fallback to default primary
    
    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);
    return `${r}, ${g}, ${b}`;
  };

  const loadTenantTheme = async () => {
    if (!activeTenantId) {
      console.log('ThemeProvider: No activeTenantId, skipping theme load');
      return;
    }
    
    console.log('ThemeProvider: Loading theme for tenant:', activeTenantId);
    
    try {
      setIsLoading(true);
      const brandingData = await apiClient.getTenantBranding(activeTenantId);
      console.log('ThemeProvider: Tenant branding loaded:', brandingData);
      
      if (brandingData.branding) {
        const branding = brandingData.branding;
        console.log('ThemeProvider: Branding found:', branding);
        
        const themeColors: ThemeColors = {
          primary: branding.primary_color || defaultColors.primary,
          secondary: branding.secondary_color || defaultColors.secondary,
          background: branding.admin_background_color || defaultColors.background,
          text: branding.web_join_text_color || defaultColors.text
        };
        
        console.log('ThemeProvider: Applying theme colors:', themeColors);
        applyTheme(themeColors);
      } else {
        console.log('ThemeProvider: No branding settings found, using defaults');
        applyTheme(defaultColors);
      }
    } catch (error) {
      console.error('ThemeProvider: Failed to load tenant theme:', error);
      // Fall back to default theme
      applyTheme(defaultColors);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const hostname = window.location.hostname;
    const isSubdomain = hostname !== 'admin.queuemein.com' && hostname.includes('.queuemein.com');
    
    // Exclude auth-related subdomains from tenant resolution
    const authSubdomains = ['auth.queuemein.com', 'login.queuemein.com', 'signin.queuemein.com'];
    const isAuthDomain = authSubdomains.includes(hostname);
    
    console.log('ThemeProvider: Hostname:', hostname, 'isSubdomain:', isSubdomain, 'isAuthDomain:', isAuthDomain, 'activeTenantId:', activeTenantId);
    
    if (isSubdomain && !isAuthDomain && activeTenantId) {
      console.log('ThemeProvider: Loading tenant theme for subdomain');
      loadTenantTheme();
    } else {
      console.log('ThemeProvider: Using default theme (admin or auth domain)');
      applyTheme(defaultColors);
    }
  }, [activeTenantId]);

  return (
    <ThemeContext.Provider value={{ colors, isLoading, applyTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}